import { ReputationTier, SkillDef, SiteDef } from '../types';
import { SKILLS } from '../data/skills';
import { SITES } from '../data/sites';
import { getRepTier } from '../utils/helpers';

export class Progression {
  reputation: number = 0;
  skillPoints: number = 0;
  unlockedSkills: Set<string> = new Set();
  completedSites: Set<string> = new Set();
  siteCompletionStars: Map<string, number> = new Map(); // siteId -> 1-3 stars
  totalArtifactsFound: number = 0;
  totalTheoriesCorrect: number = 0;

  private lastArtifactAwardCount: number = 0;
  private lastTheoryAwardCount: number = 0;

  getTier(): { tier: ReputationTier; label: string } {
    return getRepTier(this.reputation);
  }

  addReputation(amount: number): void {
    this.reputation = Math.min(100, Math.max(0, this.reputation + amount));
  }

  loseReputation(amount: number): void {
    this.reputation = Math.max(0, this.reputation - amount);
  }

  checkSkillPointAwards(): number {
    let awarded = 0;

    // 1 skill point per 5 artifacts found
    const artifactPoints = Math.floor(this.totalArtifactsFound / 5);
    const newArtifactPoints = artifactPoints - this.lastArtifactAwardCount;
    if (newArtifactPoints > 0) {
      awarded += newArtifactPoints;
      this.lastArtifactAwardCount = artifactPoints;
    }

    // 2 skill points per correct theory
    const theoryPoints = this.totalTheoriesCorrect * 2;
    const lastTheoryPoints = this.lastTheoryAwardCount * 2;
    const newTheoryPoints = theoryPoints - lastTheoryPoints;
    if (newTheoryPoints > 0) {
      awarded += newTheoryPoints;
      this.lastTheoryAwardCount = this.totalTheoriesCorrect;
    }

    this.skillPoints += awarded;
    return awarded;
  }

  canUnlockSkill(skillId: string): { canUnlock: boolean; reason: string } {
    const skill = SKILLS.find(s => s.id === skillId);
    if (!skill) {
      return { canUnlock: false, reason: 'Skill not found.' };
    }

    if (this.unlockedSkills.has(skillId)) {
      return { canUnlock: false, reason: 'Skill already unlocked.' };
    }

    if (skill.prerequisite && !this.unlockedSkills.has(skill.prerequisite)) {
      const prereq = SKILLS.find(s => s.id === skill.prerequisite);
      const prereqName = prereq ? prereq.name : skill.prerequisite;
      return { canUnlock: false, reason: `Requires prerequisite: ${prereqName}.` };
    }

    if (this.skillPoints < skill.cost) {
      return { canUnlock: false, reason: `Not enough skill points. Need ${skill.cost}, have ${this.skillPoints}.` };
    }

    return { canUnlock: true, reason: 'Ready to unlock.' };
  }

  unlockSkill(skillId: string): boolean {
    const check = this.canUnlockSkill(skillId);
    if (!check.canUnlock) return false;

    const skill = SKILLS.find(s => s.id === skillId)!;
    this.skillPoints -= skill.cost;
    this.unlockedSkills.add(skillId);
    return true;
  }

  getAvailableSkills(): SkillDef[] {
    return SKILLS.filter(skill => {
      if (this.unlockedSkills.has(skill.id)) return false;
      if (skill.prerequisite && !this.unlockedSkills.has(skill.prerequisite)) return false;
      return true;
    });
  }

  isSiteUnlocked(siteId: string): boolean {
    const site = SITES.find(s => s.id === siteId);
    if (!site) return false;
    return this.reputation >= site.repRequired;
  }

  getUnlockedSites(): SiteDef[] {
    return SITES.filter(site => this.reputation >= site.repRequired);
  }

  getEffectValue(effectKey: string): number {
    // Determine if this effect key is a multiplier or additive
    // Additive keys: those that represent flat amounts (daily_income, start_hints, etc.)
    const additiveKeys = new Set([
      'daily_income', 'start_hints', 'free_crew', 'adjacent_hint',
      'layer_highlight', 'area_dig', 'artifact_sense', 'gpr_area',
      'gpr_depth', 'nearby_pulse', 'free_full_scan', 'reveal_all',
      'auto_document', 'auto_crossref', 'restore_grade',
      'theory_eliminate', 'forecast_days', 'storm_work',
      'weather_immune', 'theory_no_penalty', 'charge_area'
    ]);

    const isAdditive = additiveKeys.has(effectKey);
    let result = isAdditive ? 0 : 1;

    for (const skillId of this.unlockedSkills) {
      const skill = SKILLS.find(s => s.id === skillId);
      if (!skill) continue;

      const pairs = skill.effect.split(',');
      for (const pair of pairs) {
        const [key, valueStr] = pair.split(':');
        if (key.trim() === effectKey) {
          const value = parseFloat(valueStr);
          if (isAdditive) {
            result += value;
          } else {
            result *= value;
          }
        }
      }
    }

    return result;
  }

  completeSite(siteId: string, completionPercent: number): { repBonus: number; stars: number } {
    const alreadyCompleted = this.completedSites.has(siteId);
    this.completedSites.add(siteId);

    // Star rating: 80%=1, 90%=2, 100%=3
    const stars = completionPercent >= 100 ? 3 : completionPercent >= 90 ? 2 : 1;
    const prevStars = this.siteCompletionStars.get(siteId) ?? 0;
    if (stars > prevStars) {
      this.siteCompletionStars.set(siteId, stars);
    }

    // Rep bonus: +25 base, +25 extra for 100% (only award on first completion)
    let repBonus = 0;
    if (!alreadyCompleted) {
      repBonus = 25;
      if (completionPercent >= 100) repBonus += 25;
      this.addReputation(repBonus);
    }

    return { repBonus, stars };
  }

  recordArtifact(): void {
    this.totalArtifactsFound++;
  }

  recordTheory(correct: boolean): void {
    if (correct) {
      this.totalTheoriesCorrect++;
    }
  }
}
